/**
 * Formats a locale string to the correct hreflang value according to BCP-47 standard.
 *
 * This function maps locale values to their corresponding hreflang values,
 * following the specific requirements for Klaviyo's hreflang implementation.
 * It normalizes case and handles special cases like German locale formatting.
 *
 * @param locale - The locale string to convert (e.g., 'de-DE', 'en-US', 'fr-FR', 'de-de', 'en-us')
 * @returns The formatted hreflang value
 *
 * @example
 * ```typescript
 * formatHreflang('de-DE')     // Returns: 'de'
 * formatHreflang('de-de')     // Returns: 'de'
 * formatHreflang('en-US')     // Returns: 'en-US'
 * formatHreflang('en-us')     // Returns: 'en-US'
 * formatHreflang('en-GB')     // Returns: 'en-GB'
 * formatHreflang('fr-FR')     // Returns: 'fr-FR'
 * formatHreflang('es-ES')     // Returns: 'es-ES'
 * formatHreflang('it-IT')     // Returns: 'it-IT'
 * formatHreflang('en-AU')     // Returns: 'en-AU'
 * formatHreflang('en-SG')     // Returns: 'en-SG'
 * ```
 */
export const formatHreflang = (locale: string): string => {
  if (!locale || typeof locale !== 'string') {
    return '';
  }
  
  // Normalize the locale to proper BCP-47 format (language-SCRIPT-REGION)
  const normalizedLocale = locale.toLowerCase().replace(/-([a-z]{2,4})/g, (_, part) => {
    // Capitalize the script or region part
    return '-' + part.toUpperCase();
  });
  
  // Special case: German locale should return just 'de' instead of 'de-DE'
  if (normalizedLocale === 'de-DE') {
    return 'de';
  }
  
  return normalizedLocale;
};
